// Scouting and intel (04 §5.10, §3.7) and their events (§7.4).
import { z } from 'zod';
import {
  facilityTypeSchema,
  idSchema,
  isoDateTimeSchema,
  resourcesSchema,
  shipCountsSchema,
} from './common.js';
import { fleetDetectionSchema } from './military.js';

const wholeUnits = z.number().int().nonnegative();
const playerRefSchema = z.strictObject({ playerId: idSchema, displayName: z.string() });

/** A system as the scout saw it (03 §3.10): facilities, stockpile, Influence, every stationed fleet. */
export const intelSnapshotSchema = z.strictObject({
  facilities: z.partialRecord(facilityTypeSchema, z.number().int().min(0).max(20)),
  resources: resourcesSchema,
  influence: wholeUnits,
  fleets: z.array(
    z.strictObject({
      kind: z.enum(['player', 'pirate']),
      ownerPlayerId: idSchema.nullable(),
      ships: shipCountsSchema,
      /** Scouting reveals stealth ships (04 §3.1 V-6). */
      stealthShips: shipCountsSchema,
    })
  ),
});
export type IntelSnapshot = z.infer<typeof intelSnapshotSchema>;

export const intelReportSchema = z.strictObject({
  id: idSchema,
  systemId: idSchema,
  systemName: z.string(),
  scoutedBy: playerRefSchema,
  takenAt: isoDateTimeSchema,
  ownerAtScoutTime: playerRefSchema.nullable(),
  snapshot: intelSnapshotSchema,
});
export type IntelReport = z.infer<typeof intelReportSchema>;

export const intelReportListQuerySchema = z.object({
  systemId: idSchema.optional(),
  limit: z.coerce.number().int().min(1).max(100).default(20),
});

export const intelReportListResponseSchema = z.strictObject({ items: z.array(intelReportSchema) });
export type IntelReportListResponse = z.infer<typeof intelReportListResponseSchema>;

export const scoutRequestSchema = z.strictObject({
  originSystemId: idSchema,
  targetSystemId: idSchema,
  /** Whole units of Influence the scout carries; they leave the origin when it is sent. */
  influence: z.number().int().positive().max(1_000_000_000),
});
export type ScoutRequest = z.infer<typeof scoutRequestSchema>;

export const SCOUT_STATUSES = ['in_transit', 'succeeded', 'failed'] as const;

export const scoutSchema = z.strictObject({
  id: idSchema,
  originSystemId: idSchema,
  targetSystemId: idSchema,
  targetSystemName: z.string(),
  influence: wholeUnits,
  status: z.enum(SCOUT_STATUSES),
  sentAt: isoDateTimeSchema,
  arrivesAt: isoDateTimeSchema,
  reportId: idSchema.nullable(),
});
export type Scout = z.infer<typeof scoutSchema>;

/** 201 with the scout, or 200 with an own report younger than `scouting.repeat_scout_cache_min`. */
export const scoutResponseSchema = z.union([
  z.strictObject({ scout: scoutSchema }),
  z.strictObject({ cachedReportId: idSchema }),
]);
export type ScoutResponse = z.infer<typeof scoutResponseSchema>;

export const scoutListResponseSchema = z.strictObject({ items: z.array(scoutSchema) });
export type ScoutListResponse = z.infer<typeof scoutListResponseSchema>;

export const detectionListResponseSchema = z.strictObject({ items: z.array(fleetDetectionSchema) });
export type DetectionListResponse = z.infer<typeof detectionListResponseSchema>;

export const scoutReturnedSchema = z.strictObject({
  scoutId: idSchema,
  targetSystemId: idSchema,
  succeeded: z.boolean(),
  reportId: idSchema.optional(),
});

/** G-38: the owner learns that a scout failed, never who sent it. */
export const scoutFailedAgainstYouSchema = z.strictObject({
  systemId: idSchema,
  influenceLost: wholeUnits,
});

export const intelReportSharedSchema = z.strictObject({
  reportId: idSchema,
  systemId: idSchema,
  takenAt: isoDateTimeSchema,
  scoutedBy: idSchema,
});
